import { useRef, useEffect } from 'react';
import constate from 'constate';
const SRC = 'thumbnails-ref.provider.ts';

import { useAppSelector } from 'store/store-hooks';
import {
  Thumbnails,
  ThumbnailData,
  ThumbnailStatus,
  selectThumbnailData,
} from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';

export type ThumbnailsRef = Record<ClipId, Base64>;

const useClipThumbProvider = () => {
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const thumbnailsRef = useRef<ThumbnailsRef>({});

  const getThumbnailRef = (clipId: ClipId) => {
    const dataUri = thumbnailsRef.current[clipId];
    if (!dataUri) {
      console.error(`[thumbnails.provider.ts] getThumbnailRef: oops`);
      return '';
    }
    return dataUri;
  };
  const addThumbnailRef = (clipId: ClipId, data: Base64) => {
    thumbnailsRef.current[clipId] = data;
  };
  const removeThumbnailRef = (clipId: ClipId) => {
    delete thumbnailsRef.current[clipId];
  };
  const clearThumbnailsRef = () => {
    thumbnailsRef.current = {};
  };
  const getThumbnailSrc = (
    clipId: CellClip | undefined,
    thumbnails: Thumbnails,
    thumbnailData?: ThumbnailData
  ): Url | Base64 | '' => {
    if (clipId === EMPTY_CELL) {
      console.error(`[${SRC}] getThumbnailSrc: clipId is EMPTY_CELL`);
      return '';
    }
    if (clipId === undefined) {
      console.error(`[${SRC}] getThumbnailSrc: clipId is undefined`);
      return '';
    }
    const thumbnail = thumbnailData ?? selectThumbnailData(clipId, thumbnails);
    if (thumbnail.status === ThumbnailStatus.init) return '';
    if (thumbnail.status === ThumbnailStatus.readyToUse) return thumbnail.url as Url;
    return getThumbnailRef(clipId);
  };

  useEffect(() => {
    return () => clearThumbnailsRef();
  }, [selectedStory]);

  return { addThumbnailRef, removeThumbnailRef, clearThumbnailsRef, getThumbnailSrc };
};

export const [ClipThumbProvider, useClipThumbContext] = constate(useClipThumbProvider);
